import Invoice from "@components/Delivery/invoices";
import ComponentContainer from "@components/Delivery/shared/components/ComponentContainer";
import DeliveryCode from "@components/Delivery/shared/components/DeliveryCode";
import { UpdateValidationDocs } from "@components/Delivery/shared/components/DeliveryFiles";
import DeliveryLogs from "@components/Delivery/shared/components/DeliveryLogs";
import SMSForm from "@components/Delivery/shared/components/SMSForm";
import SendTrackingInfoButton from "@components/Delivery/shared/components/TrackingSection";
import CustomMap from "@components/Map";
import MapInstance from "@components/Map/mapInstance";
import PreviewMissionMarkers from "@components/Map/previewMarkers";
import type { Mission } from "@components/Map/type";
import { PreviewType } from "@components/Map/type";
import { isOnGoing } from "@components/Map/utils";
import { useUserContext } from "@contexts/UserContext/index.context";
import { Box, Divider, Grid, Paper, Stack, Typography } from "@mui/material";
import type { TCancelDelivery } from "@schemas/DeliveryForm/CancelDeliverySchema";
import { useQueryClient } from "@tanstack/react-query";
import type { Doc } from "@types_def/models/delivery.types";
import { DeliveryStatus, DeliveryType } from "@types_def/models/delivery.types";
import { customDeliveryScheduledForDate, parseDocs } from "@utils/functions/Delivery";
import { strToEnum } from "@utils/strEnum";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Params } from "react-router-dom";
import { useParams } from "react-router-dom";
import LazyRender from "@/components/common/LazyRender";
import { getDeliveryDetailQueryKey } from "@/components/Delivery/cache";
import PdfSection from "@/components/Delivery/DeliveryDetails/Header/pdf-section";
import Summary from "@/components/Delivery/DeliveryFormV3/summary";
import SurveyResults from "@/components/Delivery/DeliveryRating/components/SurveyResults";
import { CancellationCard } from "@/components/Delivery/view-delivery/CancellationCard";
import { PhotosCard } from "@/components/Delivery/view-delivery/PhotosCard";
import { useFindDeliveryByIdQuery } from "@/libs/generated/hooks";
import { getDeliveryStatusFlags } from "@/utils/delivery/get-delivery-status-flags";
import ViewDeliveryActionGrid from "../components/Delivery/view-delivery-action-grid";
import {
  ActivateDeliveryAction,
  CancelDeliveryAction,
  CancelRestitutionAction,
  ChangeDriverAction,
  ConfirmDeliveryAction,
  DeleteDeliveryAction,
  EditDeliveryAction,
  SendDeliveryAction,
  SendRatingEmailAction,
  TerminateDeliveryAction,
} from "../components/Delivery/view-delivery-actions";

const cardSx = {
  backgroundColor: "#E7E6F7",
  borderRadius: 1,
  padding: "20px",
  height: "100%",
} as const;

function ViewDelivery() {
  const { t } = useTranslation();
  const { userState } = useUserContext();
  const queryClient = useQueryClient();

  const { type, contract } = useParams<Params>();
  const deliveryType = strToEnum<DeliveryType>(type as string);

  const {
    data: graphQlData,
    isPending: isLoading,
    error,
  } = useFindDeliveryByIdQuery({ GetByIdInput: { id: Number(contract) } });
  const data = graphQlData?.findDeliveryById;

  const isError = Boolean(error);

  const mission = useMemo((): Mission | null => {
    if (!data) return null;
    return {
      status: data.status,
      origin: { lng: data?.fromAddress?.longitude, lat: data?.fromAddress?.latitude },
      destination: { lng: data?.toAddress?.longitude, lat: data?.toAddress?.latitude },
      depart: data?.fromAddress?.address,
      arrive: data?.toAddress?.address,
      color: "green",
      id: data?.id.toString(),
      restitution: data?.toReturnToAddress?.address,
      toReturn: {
        lng: data?.toReturnToAddress?.longitude,
        lat: data?.toReturnToAddress?.latitude,
      },
      type: data?.type,
      destinationCity: data?.toAddress?.city,
      originCity: data?.fromAddress?.city,
      driver: isOnGoing(data?.status) && {
        id: data?.selectedDriver?.id?.toString(),
        name: `${data?.selectedDriver?.firstName} ${data?.selectedDriver?.lastName}`,
        avatar: data?.selectedDriver?.avatar,
      },
    } as Mission;
  }, [data]);

  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 1200);

  const handleResize = useCallback(() => {
    setIsMobileView(window.innerWidth < 1200);
  }, []);

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [handleResize]);

  const revalidate = () => {
    if (data?.id) {
      void queryClient.invalidateQueries({
        queryKey: getDeliveryDetailQueryKey(data.id),
        exact: true,
      });
    }
  };

  if (isLoading || !data) return <span>Loading...</span>;
  if (isError) return <span>Error: {JSON.stringify(error)}</span>;

  // ─── Derived values ────────────────────────────────────────────────────────
  const isAdmin = userState?.["custom:role"] === "ADMIN";
  const rank =
    userState?.["custom:rank"] !== undefined ? Number(userState["custom:rank"]) : undefined;

  const cancelReason = data.cancelReason as TCancelDelivery | undefined;
  const docs = parseDocs(data.docs as Doc[] | string | null | undefined);
  const validationDocs = parseDocs(data.validationDocs as Doc[] | string | null | undefined);

  const {
    isRequestedStatus,
    isCompletedStatus,
    isConfirmedStatus,
    isActiveAdminStatus,
    isInitialStatus,
    isCancelledStatus,
    isOngoingStatus,
    isDeliveryCodeVisible,
    isTwoWayDelivery,
    canManageAdminActions,
    canSeeActions,
  } = getDeliveryStatusFlags({ status: data.status, type: data.type, isAdmin, rank });

  const deliveryActionProps = {
    id: data.id,
    isAdmin,
    rank,
    status: data.status,
    type: data.type,
    cancelledAt: data.cancelledAt,
    smsSentAt: data.smsSentAt,
  };

  const generateTitle = () => {
    const courseType = data.type === DeliveryType.STANDARD ? "C1" : "CP";
    if ([DeliveryStatus.PICKUP_CLIENT].includes(data.status)) {
      return `${t("delivery.titles.delivery")} ${courseType} - № ${contract} | ${t("delivery.titles.in-progress")}`;
    }
    if ([DeliveryStatus.PICKUP_DRIVER].includes(data.status)) {
      return `${t("delivery.titles.delivery")} ${courseType} - № ${contract} | ${t("delivery.titles.driver-took-the-car")}`;
    }
    if ([DeliveryStatus.ONGOING].includes(data.status)) {
      return `${t("delivery.titles.delivery")} ${courseType} - № ${contract} | ${t("delivery.titles.car-on-the-way")}`;
    }
    return `${t("delivery.titles.delivery")} ${courseType} - № ${contract}`;
  };

  const mapContent =
    data.status === DeliveryStatus.ONGOING ? (
      <CustomMap data={[mission]} liveTracking />
    ) : (
      <MapInstance>
        <PreviewMissionMarkers
          mission={mission}
          previewType={
            deliveryType === DeliveryType.TWO_WAY_DELIVERY ? PreviewType.ABC : PreviewType.AB
          }
        />
      </MapInstance>
    );

  return (
    <Stack direction="row" sx={{ position: "relative", height: "100%", overflow: "hidden" }}>
      {!isMobileView && (
        <Box sx={{ display: { xs: "none", lg: "block" }, width: "50vw" }}>{mapContent}</Box>
      )}

      <Grid
        container
        p={3}
        spacing={3}
        sx={{ width: { xs: "100vw", lg: "50vw" }, overflowY: "scroll" }}
        width="50vw"
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <Grid size={{ xs: 12 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Stack direction="column" alignItems="center">
              <Typography
                fontWeight={600}
                position="relative"
                textAlign="center"
                textTransform="capitalize"
                variant="h4"
              >
                {generateTitle()}
              </Typography>
              <Typography textTransform="capitalize" variant="body1">
                {t("common.shared.delivery-scheduled-for")}{" "}
                {data.carDeliveryDeadline
                  ? customDeliveryScheduledForDate(data.carDeliveryDeadline)
                  : "P/A"}
              </Typography>
            </Stack>
            {isAdmin && isCompletedStatus ? (
              <PdfSection
                ready={data.vehicles.every(
                  (vehicle) => vehicle.imagesAfter && vehicle.imagesBefore
                )}
              />
            ) : null}
          </Stack>
          <Divider sx={{ margin: 1 }} />
          {isAdmin && DeliveryStatus.ONGOING === data?.status ? (
            <Grid size={{ xs: 12 }}>
              <SendTrackingInfoButton id={data.id.toString()} />
            </Grid>
          ) : null}
        </Grid>

        {/* ── Map (mobile) ────────────────────────────────────────────────── */}
        {isMobileView && (
          <Grid sx={{ display: { sm: "block", lg: "none", minHeight: 400 } }} size={{ xs: 12 }}>
            {mapContent}
          </Grid>
        )}
        {/* ── Sections ────────────────────────────────────────────────────── */}

        {isCancelledStatus && (
          <CancellationCard
            status={data.status}
            cancelledAt={data.cancelledAt}
            cancelReason={cancelReason}
          />
        )}

        <PhotosCard delivery={data} docs={docs} validationDocs={validationDocs} />

        {isAdmin && isCompletedStatus && (
          <Grid size={{ xs: 12 }}>
            <Paper elevation={4} sx={cardSx}>
              <ComponentContainer title={t("common.shared.invoices")}>
                <Invoice invoiceUrl={data.invoice ?? ""} />
              </ComponentContainer>
            </Paper>
          </Grid>
        )}

        {isDeliveryCodeVisible && (
          <Grid size={{ xs: 12 }}>
            <Paper elevation={4} sx={cardSx}>
              <DeliveryCode startCode={data.startCode} destinationCode={data.destinationCode} />
            </Paper>
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Paper elevation={4} sx={cardSx}>
            <ComponentContainer title={t("common.shared.summary")}>
              <Summary data={data} isRestitution={isTwoWayDelivery} />
            </ComponentContainer>
          </Paper>
        </Grid>

        {isCompletedStatus && (
          <Grid size={{ xs: 12 }}>
            <Paper elevation={4} sx={cardSx}>
              <ComponentContainer title={t("common.shared.survey-results")}>
                {data.rated ? (
                  <SurveyResults deliveryId={data.id} />
                ) : (
                  <Typography variant="body2">{t("common.shared.not-rated")}</Typography>
                )}
              </ComponentContainer>
            </Paper>
          </Grid>
        )}

        {isRequestedStatus && isAdmin && (
          <Grid size={{ xs: 12 }}>
            <Paper elevation={4} sx={cardSx}>
              <ComponentContainer title="SMS">
                <SMSForm
                  data={data as unknown as import("@types_def/models/delivery.types").Delivery}
                  isRestitution={isTwoWayDelivery}
                />
              </ComponentContainer>
            </Paper>
          </Grid>
        )}

        {isAdmin && (
          <Grid size={{ xs: 12 }}>
            <Paper elevation={4} sx={cardSx}>
              <ComponentContainer title="Delivery logs">
                <LazyRender>
                  <DeliveryLogs deliveryId={Number(contract)} />
                </LazyRender>
              </ComponentContainer>
            </Paper>
          </Grid>
        )}

        {canManageAdminActions && isCompletedStatus && (
          <UpdateValidationDocs data={data} revalidate={revalidate} />
        )}

        {/* ── Actions ─────────────────────────────────────────────────────── */}
        {canSeeActions && (
          <Grid container spacing={1} size={{ xs: 12 }}>
            <ViewDeliveryActionGrid>
              {isRequestedStatus && isTwoWayDelivery && canManageAdminActions ? (
                <CancelRestitutionAction {...deliveryActionProps} />
              ) : null}
              {isRequestedStatus || isCompletedStatus || isActiveAdminStatus || isOngoingStatus ? (
                <CancelDeliveryAction {...deliveryActionProps} />
              ) : null}
              {isRequestedStatus || isActiveAdminStatus || isOngoingStatus ? (
                <EditDeliveryAction {...deliveryActionProps} />
              ) : null}
              {isRequestedStatus && canManageAdminActions ? (
                <ConfirmDeliveryAction {...deliveryActionProps} />
              ) : null}
              {(isRequestedStatus || isActiveAdminStatus || isCancelledStatus) &&
              canManageAdminActions ? (
                <DeleteDeliveryAction {...deliveryActionProps} />
              ) : null}
              {isCompletedStatus && canManageAdminActions ? (
                <SendRatingEmailAction {...deliveryActionProps} />
              ) : null}
              {isActiveAdminStatus && isTwoWayDelivery && canManageAdminActions ? (
                <CancelRestitutionAction {...deliveryActionProps} />
              ) : null}
              {(isActiveAdminStatus || isOngoingStatus) && canManageAdminActions ? (
                <TerminateDeliveryAction {...deliveryActionProps} />
              ) : null}
              {isConfirmedStatus && canManageAdminActions ? (
                <SendDeliveryAction {...deliveryActionProps} />
              ) : null}
              {!isInitialStatus && canManageAdminActions ? (
                <ChangeDriverAction {...deliveryActionProps} />
              ) : null}
              {isCancelledStatus ? <ActivateDeliveryAction {...deliveryActionProps} /> : null}
            </ViewDeliveryActionGrid>
          </Grid>
        )}
      </Grid>
    </Stack>
  );
}

export default ViewDelivery;
